import APIService from '@/api';
import { app } from '@/main';
import {
  LogsInitConfiguration,
  datadogLogs,
  StatusType
} from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import {
  onLCP,
  onFID,
  onCLS,
  CLSMetric,
  FIDMetric,
  LCPMetric
} from 'web-vitals';

import DateUtils from './DateUtils';
import isRemoteLoading from './remote-loading';

const PERFOMANCE_TIMER_STORAGE = '@twism:';

let webVitalContext: any = {};

const triggerWebVitalEvent = (event: CLSMetric | FIDMetric | LCPMetric) => {
  webVitalContext[event.name] = event;
  datadogRum.setGlobalContext(webVitalContext);
};

export const initWebVitals = () => {
  onCLS(triggerWebVitalEvent);
  onFID(triggerWebVitalEvent);
  onLCP(triggerWebVitalEvent);
};

export const initDatadogLibrary = () => {
  const urlParams = new URLSearchParams(location.search);

  if (urlParams.has('disableDatadog')) return;

  if (!APIService.isSuperAdmin() && !isRemoteLoading()) {
    datadogLogs.init({
      clientToken: process.env.VUE_APP_DATADOG_LOG_CLIENT_TOKEN || '',
      site: 'datadoghq.com',
      forwardErrorsToLogs: process.env.VUE_APP_ENV === 'production',
      sessionSampleRate: 100,
      env: process.env.VUE_APP_ENV,
      service: `twism-${process.env.VUE_APP_ENV}-dashboard`,
      beforeSend: log => {
        // discard 404 network errors
        if (log.http && log.http.status_code === 404) {
          return false;
        }

        // discard ad blocker errors
        if (log.http && log.http.url.includes('https://api.segment.io')) {
          return false;
        }

        if (
          log.message &&
          log.message.includes('Segment snippet included twice.')
        ) {
          return false;
        }
      }
    } as LogsInitConfiguration);
    datadogRum.init({
      applicationId: process.env.VUE_APP_DATADOG_RUM_APPLICATION_ID as string,
      clientToken: process.env.VUE_APP_DATADOG_RUM_CLIENT_TOKEN as string,
      site: 'datadoghq.com',
      env: process.env.VUE_APP_ENV,
      service: `twism-${process.env.VUE_APP_ENV}-dashboard`,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 0,
      trackUserInteractions: false,
      trackResources: true,
      trackLongTasks: true
    });
  }
};

const logger = {
  reset() {
    datadogLogs.clearUser();
    datadogRum.clearUser();
  },
  identifyBusiness(id?: string) {
    const obj = {
      id: id || app.$store.getters['business/getBusinessState'].id
    };
    datadogLogs.setUser(obj);
    datadogRum.setUser(obj);
  },
  log(message: string, status?: StatusType) {
    datadogLogs.logger.log(message, {}, status);
  },
  error(message: string) {
    datadogLogs.logger.error(message);
  },
  debug(message: string) {
    datadogLogs.logger.debug(message);
  },
  info(message: string, messageContext?: any) {
    datadogLogs.logger.info(message, messageContext);
  },
  warn(message: string) {
    datadogLogs.logger.warn(message);
  },
  perfomanceTimerStart(key: string, secondsExceptionLimit: number) {
    const storageName = `${PERFOMANCE_TIMER_STORAGE}${key}`;

    if (!sessionStorage.getItem(storageName))
      sessionStorage.setItem(
        storageName,
        JSON.stringify({
          date: new Date().toISOString(),
          secondsExceptionLimit
        })
      );
  },
  perfomanceTimerEnd(key: string) {
    const storageName = `${PERFOMANCE_TIMER_STORAGE}${key}`;
    if (!sessionStorage.getItem(storageName)) return;

    const startTimer = JSON.parse(sessionStorage.getItem(
      storageName
    ) as string);
    const endTimer = new Date().toISOString();

    sessionStorage.removeItem(storageName);

    const seconds = DateUtils.getDifferenceSeconds(startTimer.date, endTimer);

    if (seconds > startTimer.secondsExceptionLimit) {
      logger.error(
        `Event "${key}" took longer (${seconds}s) than expected to be exception limit: ${
          startTimer.secondsExceptionLimit
        }s`
      );
    }

    return seconds;
  }
};

export default logger;
